/**
 * The site admin API, `/api/admin/*` (docs/specs/accounts.md, sections 4 and 8), and nothing
 * else. Every request and response shape of that contract is in this one file, so a change on the
 * server side (apps/server/src/site-admin/) is a change here and nowhere else.
 *
 * Every call goes through `apiFetch`, so a 401 ends the login and sends the person to `#/login`
 * like a 401 from any other request. A refusal throws an `ApiError` whose `serverMessage` holds
 * the server's own words — the guard messages ("this is the only enabled admin…") are written to
 * be shown as they are.
 *
 * A `setup_path` is a secret that the server shows once. This module hands it to the caller and
 * keeps no copy: no cache, no storage, no log.
 */
import { apiFetch } from '../api.ts';
import type { Role } from '../auth/authApi.ts';
import { MUTATING_HEADER, apiErrorFrom } from '../lib/http.ts';
import type { RunStatus } from '../types.ts';

export interface AdminUser {
  id: string;
  email: string;
  role: Role;
  /** null = no limit. */
  monthly_run_quota: number | null;
  disabled: boolean;
  /** False until the setup link is used, and again after "reset password". */
  has_password: boolean;
  created_at: string;
  /** The refund-aware count: the same number the person's own header is built from. */
  runs_used_this_month: number;
  /** An unused, unexpired setup link exists for this user. */
  setup_link_outstanding: boolean;
}

/** A user together with the one-time path of their setup link: `/#/setup/<token>`. */
export interface UserWithSetupPath {
  user: AdminUser;
  setup_path: string;
}

export interface NewUser {
  email: string;
  role: Role;
  /** Left out: the role's default — 5 for a user, no limit for an admin. */
  monthly_run_quota?: number | null;
}

export interface UserPatch {
  role?: Role;
  monthly_run_quota?: number | null;
  disabled?: boolean;
}

export interface AdminRun {
  id: string;
  session_id: string;
  /** Null for a session that nobody owns, such as a run made by `evals/enqueue-run.ts`. */
  owner_email: string | null;
  status: RunStatus;
  started_at: string;
  updated_at: string;
  cost_usd: number;
  over_cost: boolean;
  report_credit: 'refund_due' | null;
  error: string | null;
  stages: {
    competitors: number;
    competitors_done: number;
    competitors_failed: number;
    compare_status: string | null;
  };
  has_artifact: boolean;
}

export interface AdminHealth {
  worker: 'ok' | 'absent';
  queue: { pending: number; claimed: number };
  /** `free_bytes` is null when the server could not read the volume. */
  disk: { free_bytes: number | null; min_free_bytes: number };
  runs: {
    total: number;
    by_status: Record<RunStatus, number>;
    refund_due: number;
    over_cost: number;
  };
}

const JSON_HEADERS = { ...MUTATING_HEADER, 'content-type': 'application/json' } as const;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(path, init);
  if (!res.ok) throw await apiErrorFrom(res);
  return (await res.json()) as T;
}

const userPath = (id: string): string => `/api/admin/users/${encodeURIComponent(id)}`;

export function listUsers(): Promise<AdminUser[]> {
  return request('/api/admin/users');
}

/** 400: a bad email, role, or quota. 409: the email already has a user. */
export function createUser(input: NewUser): Promise<UserWithSetupPath> {
  return request('/api/admin/users', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(input) });
}

/** 409: a guard refused — the only enabled admin, or the admin's own account. 404: no such user. */
export async function updateUser(id: string, patch: UserPatch): Promise<AdminUser> {
  const body = await request<{ user: AdminUser }>(userPath(id), {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(patch),
  });
  return body.user;
}

/** "Reset password". 409: the admin's own account, or a disabled user. 404: no such user. */
export function makeSetupLink(id: string): Promise<UserWithSetupPath> {
  return request(`${userPath(id)}/setup-link`, { method: 'POST', headers: MUTATING_HEADER });
}

/** Every run, newest first. The server does not page this list yet. */
export function listRuns(): Promise<AdminRun[]> {
  return request('/api/admin/runs');
}

export function getHealth(): Promise<AdminHealth> {
  return request('/api/admin/health');
}
